import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from 'lib/utils';
import { useTelegramMiniAppLogin } from 'modules/auth';
import { isTelegram, getInitData } from 'lib/telegram';
import { socialButtonClassName } from './socialButton';
import { TelegramLogo } from './TelegramLogo';

interface TelegramMiniAppButtonProps {
  /** Internal path to land on after a successful sign-in. */
  redirectTo?: string;
  accountType?: 'STAFF' | 'CANDIDATE';
  /** 'login' never creates an account — the API answers ACCOUNT_NOT_FOUND instead. */
  mode?: 'login' | 'signup';
  /** Optional custom button label */
  label?: string;
  children?: React.ReactNode;
  /** Optional badge text displayed on the right (e.g. "Tezkor") */
  badge?: string;
  /** Extra classes for the outer container. */
  className?: string;
}

/** Telegram brand paper-plane glyph (lucide has no brand mark). */

/** Renders nothing outside Telegram (no initData) — browser Login/Signup use <TelegramBotLogin> instead. */
export const TelegramMiniAppButton: React.FC<TelegramMiniAppButtonProps> = ({
  redirectTo = '/app',
  accountType,
  mode,
  label,
  children,
  badge,
  className,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const login = useTelegramMiniAppLogin();
  const [error, setError] = React.useState<string | null>(null);

  if (!isTelegram() || !getInitData()) return null;

  const onClick = async () => {
    // Guard against a double-tap while the exchange is in flight.
    if (login.isPending) return;
    setError(null);
    try {
      await login.mutateAsync({
        initData: getInitData(),
        accountType,
        mode,
      });
      navigate(redirectTo, { replace: true });
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { code?: string; message?: string } } })?.response?.data;
      setError(body?.code === 'ACCOUNT_NOT_FOUND' ? t('auth.no_account_signup') : body?.message || t('auth.telegram_failed'));
    }
  };

  return (
    <div className={cn('flex flex-col items-center gap-3 w-full', className)}>
      <button type="button" className={socialButtonClassName} onClick={onClick} disabled={login.isPending}>
        {login.isPending ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        ) : (
          <TelegramLogo className="h-[18px] w-[18px] shrink-0" />
        )}
        <span>{children ?? label ?? t('auth.continue_with_telegram')}</span>
        {badge && (
          <span className="ml-auto rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/80">{badge}</span>
        )}
      </button>
      {error ? (
        <p role="alert" className="text-center text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default TelegramMiniAppButton;
